import { Link } from "react-router-dom";
import { ArrowRight, Users, MessageSquareQuote } from "lucide-react";
import QuestionBadge from "./QuestionBadge";
import TiltCard from "./TiltCard";



export type MatchedAnswer = {
  id: string;
  slug: string;
  matched_question: string;
  similarity: number;
  direct_answer: string | null;
  tldr_bullets: string[];
  episode_count: number;
  cited_hosts: Array<{ name?: string } | string>;
  theme: string | null;
};

export type CalloutTier = "strong" | "good" | "loose";

export function getMatchedAnswerTier(similarity: number | null | undefined): CalloutTier | null {
  if (similarity == null) return null;
  if (similarity >= 0.75) return "strong";
  if (similarity >= 0.55) return "good";
  if (similarity >= 0.35) return "loose";
  return null;
}

const TIER_COPY: Record<CalloutTier, { label: string; lead: string }> = {
  strong: {
    label: "Direct answer to your question",
    lead: "We've already written a deep answer to this exact question.",
  },
  good: {
    label: "Closely related answered question",
    lead: "We've answered a very similar question in depth.",
  },
  loose: {
    label: "Related answered question",
    lead: "Not a direct match, but you might find this useful.",
  },
};

function hostNames(cited: MatchedAnswer["cited_hosts"]): string[] {
  return cited
    .map((c) => (typeof c === "string" ? c : c?.name))
    .filter((n): n is string => Boolean(n && n.trim()));
}

export default function AnsweredQuestionCallout({
  match,
  tier,
}: {
  match: MatchedAnswer;
  tier: CalloutTier;
}) {
  const copy = TIER_COPY[tier];
  const hosts = hostNames(match.cited_hosts);
  const isStrong = tier === "strong";

  const snippet = (match.direct_answer || match.tldr_bullets[0] || "").trim();

  return (
    <TiltCard className="hover-lift transition-all duration-200">
      <Link
        to={`/answer/${match.slug}`}
        className={[
          "group relative block rounded-md p-4 sm:p-5 pl-5 sm:pl-7 overflow-hidden",
          // Distinct peach/orange tint — visually a different object class than podcast cards
          "bg-[hsl(var(--primary)/0.08)]",
          // Dashed border + primary color signals "note / answer" vs solid foreground podcast cards
          "border-2 border-dashed border-primary/60",
          "shadow-[4px_4px_0_0_hsl(var(--primary)/0.35)] hover:shadow-[6px_6px_0_0_hsl(var(--primary)/0.5)]",
          "hover:-translate-x-[2px] hover:-translate-y-[2px] transition-all",
          isStrong ? "bg-[hsl(var(--primary)/0.14)]" : "",
        ].join(" ")}
        aria-label={`Read full answer: ${match.matched_question}`}
      >
        {/* Left accent stripe — full-height solid primary bar */}
        <span
          aria-hidden
          className="absolute left-0 top-0 bottom-0 w-1.5 bg-primary"
        />

        {/* Decorative quote mark in corner — instantly signals "written answer" */}
        <MessageSquareQuote
          aria-hidden
          className="absolute -top-2 -right-2 w-20 h-20 text-primary/10 rotate-12 pointer-events-none"
        />

        <div className="flex items-center gap-2 mb-2 relative">
          <QuestionBadge label={copy.label} icon="sparkles" />
          <span className="font-mono text-[10px] text-muted-foreground hidden sm:inline">
            {(match.similarity * 100).toFixed(0)}% match
          </span>
        </div>

        <p className="font-mono text-[11px] sm:text-xs text-muted-foreground mb-1 relative">
          {copy.lead}
        </p>

        <h3
          className={[
            "question-title",
            "mb-2 relative",
            isStrong ? "text-lg sm:text-xl" : "text-base sm:text-lg",
          ].join(" ")}
        >
          {match.matched_question}
        </h3>

        {snippet && (
          <p className="text-sm sm:text-base text-foreground/90 leading-snug mb-3 line-clamp-3 relative">
            {snippet}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mb-3 text-[11px] sm:text-xs font-mono text-muted-foreground relative">
          {match.episode_count > 0 && (
            <span>Drawn from {match.episode_count} episode{match.episode_count === 1 ? "" : "s"}</span>
          )}
          {hosts.length > 0 && (
            <span className="inline-flex items-center gap-1">
              <Users className="w-3 h-3" aria-hidden />
              {hosts.slice(0, 3).join(", ")}
              {hosts.length > 3 ? ` +${hosts.length - 3}` : ""}
            </span>
          )}
        </div>

        <span className="inline-flex items-center gap-1.5 font-mono text-xs sm:text-sm font-bold text-primary group-hover:underline relative">
          Read the full answer
          <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" aria-hidden />
        </span>
      </Link>
    </TiltCard>
  );
}

